import { FC, memo, useEffect, useRef } from "react";
import { TENNIS_VIEW_H, TENNIS_VIEW_W } from "../positioning";
import TennisBallSvg from "../assets/tennis-ball.svg";

export type TrailDirection = "left" | "right";

export type ServeTrailData = {
  id: number;
  from: { x: number; y: number };
  to: { x: number; y: number };
  direction: TrailDirection;
};

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

const ARC_DRAW_MS = 350;
const LINE_DRAW_DELAY_MS = 100;
const LINE_DRAW_MS = 250;
const LINE_FADE_DELAY_MS = 500;
const SHADOW_FADE_DELAY_MS = 550;
const SHADOW_FADE_MS = 400;
const SHADOW_PEAK_DELAY_MS = 120;
const SHADOW_PEAK_MS = 150;
const BALL_SHOW_DELAY_MS = 150;
const BALL_SHOW_MS = 120;
const BALL_FADE_DELAY_MS = 500;
const BALL_FADE_MS = 350;

export const ServeTrailIndicator: FC<{ trail: ServeTrailData }> = memo(
  ({ trail }) => {
    const { from, to, direction, id } = trail;
    const dirSign = direction === "right" ? 1 : -1;

    const startX = clamp(from.x, 0, 1) * TENNIS_VIEW_W;
    const startY = clamp(from.y, 0, 1) * TENNIS_VIEW_H;
    const endX = clamp(to.x, 0, 1) * TENNIS_VIEW_W;
    const endY = clamp(to.y, 0, 1) * TENNIS_VIEW_H;

    const travelDistance = Math.max(
      TENNIS_VIEW_W * 0.12,
      Math.hypot(endX - startX, endY - startY)
    );

    const lineLength = clamp(
      travelDistance * 0.35,
      TENNIS_VIEW_W * 0.08,
      TENNIS_VIEW_W * 0.22
    );

    const bounceX = clamp(
      endX - dirSign * lineLength,
      TENNIS_VIEW_W * 0.05,
      TENNIS_VIEW_W * 0.95
    );
    const bounceY = clamp(endY + TENNIS_VIEW_H * 0.08, 0, TENNIS_VIEW_H);

    const apexY = clamp(
      Math.min(startY, bounceY) - TENNIS_VIEW_H * 0.22,
      0,
      TENNIS_VIEW_H * 0.75
    );
    const controlX = (startX + bounceX) / 2;

    const arcPath = `M ${startX.toFixed(2)} ${startY.toFixed(
      2
    )} Q ${controlX.toFixed(2)} ${apexY.toFixed(2)} ${bounceX.toFixed(
      2
    )} ${bounceY.toFixed(2)}`;
    const linePath = `M ${bounceX.toFixed(2)} ${bounceY.toFixed(
      2
    )} L ${endX.toFixed(2)} ${endY.toFixed(2)}`;

    const gradientId = `tennis-serve-arc-${id}`;

    const shadowY = clamp(bounceY + TENNIS_VIEW_H * 0.025, 0, TENNIS_VIEW_H);
    const shadowRx = clamp(
      Math.abs(endX - bounceX) * 0.6,
      TENNIS_VIEW_W * 0.02,
      TENNIS_VIEW_W * 0.07
    );
    const shadowRy = clamp(
      shadowRx * 0.35,
      TENNIS_VIEW_H * 0.005,
      TENNIS_VIEW_H
    );

    const ballTrailRadius = clamp(
      TENNIS_VIEW_W * 0.016,
      TENNIS_VIEW_W * 0.012,
      TENNIS_VIEW_W * 0.02
    );

    const arcLengthApprox =
      Math.hypot(controlX - startX, apexY - startY) +
      Math.hypot(bounceX - controlX, bounceY - apexY);

    const arcPathRef = useRef<SVGPathElement | null>(null);
    const linePathRef = useRef<SVGPathElement | null>(null);
    const shadowRef = useRef<SVGEllipseElement | null>(null);
    const ballImageRef = useRef<SVGImageElement | null>(null);
    const timeoutIdsRef = useRef<number[]>([]);
    const rafIdsRef = useRef<number[]>([]);

    const clearScheduled = () => {
      timeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
      timeoutIdsRef.current = [];
      rafIdsRef.current.forEach((id) => window.cancelAnimationFrame(id));
      rafIdsRef.current = [];
    };

    useEffect(() => {
      clearScheduled();

      const arcEl = arcPathRef.current;
      if (arcEl) {
        try {
          const len = arcEl.getTotalLength();
          arcEl.style.strokeDasharray = `${len}`;
          arcEl.style.strokeDashoffset = `${len}`;
          arcEl.style.transition = "none";
          arcEl.getBoundingClientRect();
          const rafId = window.requestAnimationFrame(() => {
            arcEl.style.transition = `stroke-dashoffset ${ARC_DRAW_MS}ms ease-out`;
            arcEl.style.strokeDashoffset = "0";
          });
          rafIdsRef.current.push(rafId);
        } catch {
          arcEl.style.strokeDasharray = `${arcLengthApprox}`;
          arcEl.style.strokeDashoffset = "0";
        }
      }

      const lineEl = linePathRef.current;
      if (lineEl) {
        try {
          const len = lineEl.getTotalLength();
          lineEl.style.strokeDasharray = `${len}`;
          lineEl.style.strokeDashoffset = `${len}`;
        } catch {
          lineEl.style.strokeDasharray = "1";
          lineEl.style.strokeDashoffset = "1";
        }
        lineEl.style.opacity = "1";
        lineEl.style.transition = "none";
        lineEl.getBoundingClientRect();
        const drawTimeout = window.setTimeout(() => {
          lineEl.style.transition = `stroke-dashoffset ${LINE_DRAW_MS}ms ease-out`;
          lineEl.style.strokeDashoffset = "0";
        }, LINE_DRAW_DELAY_MS);
        timeoutIdsRef.current.push(drawTimeout);

        const fadeTimeout = window.setTimeout(() => {
          lineEl.style.transition = `opacity ${BALL_FADE_MS}ms ease-in`;
          lineEl.style.opacity = "0";
        }, LINE_FADE_DELAY_MS);
        timeoutIdsRef.current.push(fadeTimeout);
      }

      const shadowEl = shadowRef.current;
      if (shadowEl) {
        shadowEl.style.opacity = "0";
        shadowEl.style.transition = "none";
        shadowEl.getBoundingClientRect();
        const showShadowTimeout = window.setTimeout(() => {
          shadowEl.style.transition = `opacity ${SHADOW_PEAK_MS}ms ease-out`;
          shadowEl.style.opacity = "0.45";
        }, SHADOW_PEAK_DELAY_MS);
        timeoutIdsRef.current.push(showShadowTimeout);

        const hideShadowTimeout = window.setTimeout(() => {
          shadowEl.style.transition = `opacity ${SHADOW_FADE_MS}ms ease-in`;
          shadowEl.style.opacity = "0";
        }, SHADOW_FADE_DELAY_MS);
        timeoutIdsRef.current.push(hideShadowTimeout);
      }

      const ballEl = ballImageRef.current;
      if (ballEl) {
        ballEl.style.opacity = "0";
        ballEl.style.transition = "none";
        ballEl.getBoundingClientRect();
        const showBallTimeout = window.setTimeout(() => {
          ballEl.style.transition = `opacity ${BALL_SHOW_MS}ms ease-out`;
          ballEl.style.opacity = "1";
        }, BALL_SHOW_DELAY_MS);
        timeoutIdsRef.current.push(showBallTimeout);

        const hideBallTimeout = window.setTimeout(() => {
          ballEl.style.transition = `opacity ${BALL_FADE_MS}ms ease-in`;
          ballEl.style.opacity = "0";
        }, BALL_FADE_DELAY_MS);
        timeoutIdsRef.current.push(hideBallTimeout);
      }

      return clearScheduled;
    }, [arcLengthApprox, trail.id]);

    useEffect(() => clearScheduled, []);

    return (
      <div
        className="animate-serve-trail pointer-events-none absolute inset-0 z-[999999998] flex items-center justify-center"
        style={{
          transform: "perspective(-350px) rotateX(50deg)",
          transformStyle: "preserve-3d",
        }}
      >
        <svg
          className="pointer-events-none h-full w-full overflow-visible"
          viewBox={`0 0 ${TENNIS_VIEW_W} ${TENNIS_VIEW_H}`}
        >
          <defs>
            <linearGradient
              id={gradientId}
              x1={startX}
              y1={startY}
              x2={bounceX}
              y2={bounceY}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="white" stopOpacity="1" />
              <stop offset="1" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>

          <path
            d={arcPath}
            stroke={`url(#${gradientId})`}
            strokeWidth={3}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={arcLengthApprox}
            strokeDashoffset={arcLengthApprox}
            ref={arcPathRef}
          />

          <ellipse
            cx={bounceX}
            cy={shadowY}
            rx={shadowRx}
            ry={shadowRy}
            fill="rgba(0,0,0,0.45)"
            opacity={0}
            ref={shadowRef}
          />

          <path
            d={linePath}
            stroke="white"
            strokeWidth={3}
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray="1"
            strokeDashoffset="1"
            ref={linePathRef}
          />

          <image
            href={TennisBallSvg}
            x={endX - ballTrailRadius}
            y={endY - ballTrailRadius}
            width={ballTrailRadius * 2.2}
            height={ballTrailRadius * 2.2}
            opacity={0}
            ref={ballImageRef}
            className="pointer-events-none"
          />
        </svg>
      </div>
    );
  }
);
